import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { thaiDateTime } from '../lib/format'

type ControlStatus = 'implemented' | 'in_progress' | 'planned'

interface TrustProfile {
  headline: string
  statement: string
  certifications: string[]
  dpo_email: string
  updated_at?: string
}

interface TrustControl {
  id?: string
  code: string
  title_th: string
  status: ControlStatus
  evidence: string
  updated_at?: string
}

interface TrustPayload {
  tenant?: string
  profile: TrustProfile
  controls: TrustControl[]
  generated_at: string
}

const statusMeta: Record<ControlStatus, { label: string; color: string }> = {
  implemented: { label: 'ใช้งานจริง', color: 'var(--color-emerald-soft)' },
  in_progress: { label: 'กำลังดำเนินการ', color: 'var(--color-amber)' },
  planned: { label: 'ตามแผน', color: 'var(--color-ivory-dim)' },
}

function resolveTenant(): string {
  const fromQuery = new URLSearchParams(window.location.search).get('tenant')?.trim()
  if (fromQuery) return fromQuery
  const fromEnv = import.meta.env.VITE_TENANT_SLUG as string | undefined
  return fromEnv?.trim() || 'demo-audit'
}

export function TrustCenter() {
  const [tenant] = useState(resolveTenant)
  const [view, setView] = useState<TrustPayload | null>(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const ctrl = new AbortController()
    fetch(`/v1/trust?tenant=${encodeURIComponent(tenant)}`, {
      signal: ctrl.signal,
      headers: { Accept: 'application/json' },
    })
      .then(async (res) => {
        const body = await res.json().catch(() => null)
        if (!res.ok) throw new Error(body?.detail || body?.title || 'โหลดข้อมูลไม่สำเร็จ')
        return body.data as TrustPayload
      })
      .then(setView)
      .catch((e: Error) => {
        if (e.name !== 'AbortError') setErr(e.message)
      })
      .finally(() => setLoading(false))
    return () => ctrl.abort()
  }, [tenant])

  if (loading) {
    return (
      <div className="public-wrap">
        <div className="dim" style={{ fontSize: 12.5 }}>กำลังโหลดข้อมูลความมั่นคงปลอดภัย…</div>
      </div>
    )
  }

  if (err || !view) {
    return (
      <div className="public-wrap">
        <div style={{ maxWidth: 460, textAlign: 'center' }}>
          <div className="eyebrow">ศูนย์ความโปร่งใส</div>
          <h1 style={{ margin: '14px 0 10px', fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 300 }}>
            ยังเปิดหน้านี้ไม่ได้
          </h1>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.7 }}>{err || 'ไม่พบข้อมูลขององค์กรนี้'}</p>
          <p className="dim" style={{ fontSize: 11.5, marginTop: 18 }}>
            ตรวจสอบลิงก์อีกครั้ง หรือติดต่อผู้ดูแลระบบขององค์กร
          </p>
        </div>
      </div>
    )
  }

  const { profile, controls } = view
  const implemented = controls.filter((c) => c.status === 'implemented').length

  return (
    <div className="public-wrap">
      <div style={{ width: '100%', maxWidth: 960 }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 34 }}>
          <div className="row" style={{ gap: 10 }}>
            <span className="brand-mark" aria-hidden="true" />
            <span className="eyebrow">AUDITAI · ศูนย์ความโปร่งใส</span>
          </div>
          <Link className="dim" to="/login" style={{ fontSize: 11.5 }}>สำหรับผู้ดูแลระบบ</Link>
        </div>

        <div style={{ paddingBottom: 30, borderBottom: '1px solid var(--color-hairline)' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(30px, 4vw, 46px)',
            lineHeight: 1.12,
            letterSpacing: '-0.02em',
            fontWeight: 300,
            maxWidth: 760,
          }}>{profile.headline}</h1>
          <p className="muted" style={{ marginTop: 18, fontSize: 14.5, lineHeight: 1.85, maxWidth: 720 }}>
            {profile.statement}
          </p>

          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 22 }}>
            {profile.certifications.length > 0
              ? profile.certifications.map((c) => <span key={c} className="chip gold">{c}</span>)
              : <span className="dim" style={{ fontSize: 11.5 }}>ยังไม่ประกาศการรับรองมาตรฐานในขณะนี้</span>}
          </div>

          <div className="row dim" style={{ gap: 18, flexWrap: 'wrap', marginTop: 18, fontSize: 11.5 }}>
            {profile.dpo_email && (
              <span>ผู้คุ้มครองข้อมูลส่วนบุคคล · <a href={`mailto:${profile.dpo_email}`} className="gold">{profile.dpo_email}</a></span>
            )}
            <span>ปรับปรุงล่าสุด {profile.updated_at ? thaiDateTime(profile.updated_at) : thaiDateTime(view.generated_at)}</span>
          </div>
        </div>

        <div className="row" style={{ justifyContent: 'space-between', margin: '30px 0 14px' }}>
          <span className="eyebrow">มาตรการความมั่นคงปลอดภัย</span>
          <span className="dim tnum" style={{ fontSize: 11.5 }}>{implemented}/{controls.length} ใช้งานจริง</span>
        </div>

        <div style={{ border: '1px solid var(--color-hairline)', borderRadius: 'var(--radius-lg)', background: 'var(--color-wash)', overflow: 'hidden' }}>
          {controls.map((c, i) => {
            const meta = statusMeta[c.status] ?? statusMeta.planned
            return (
              <div
                key={c.code}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '10px minmax(0, 1fr) auto',
                  gap: 14,
                  alignItems: 'start',
                  padding: '17px 18px',
                  borderTop: i === 0 ? 'none' : '1px solid var(--color-hairline)',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{ width: 7, height: 7, borderRadius: '50%', background: meta.color, marginTop: 7 }}
                />
                <div>
                  <div style={{ fontSize: 13.5 }}>{c.title_th}</div>
                  {c.evidence && (
                    <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.7, marginTop: 5 }}>{c.evidence}</div>
                  )}
                </div>
                <span className="mono" style={{ fontSize: 10.5, color: meta.color, whiteSpace: 'nowrap', marginTop: 2 }}>
                  {meta.label}
                </span>
              </div>
            )
          })}
        </div>

        <div className="dim" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 22, fontSize: 11.5 }}>
          <span>ข้อมูลชุดเดียวกันนี้เผยแพร่แบบ machine-readable ที่</span>
          <code className="mono" style={{ padding: '3px 8px', border: '1px solid var(--color-hairline)', borderRadius: 'var(--radius-sm)' }}>
            GET /v1/trust?tenant={tenant}
          </code>
        </div>

        <div className="dim" style={{ marginTop: 26, fontSize: 11, lineHeight: 1.7 }}>
          สร้างเมื่อ {thaiDateTime(view.generated_at)} · สถานะสะท้อนการทำงานจริงของระบบ ไม่ใช่การรับรองจากบุคคลที่สาม
        </div>
      </div>
    </div>
  )
}
